import { Chip, Tooltip } from '@mui/material'
import type { GridCellParams, GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid'
import type { TFunction } from 'i18next'
import type { DateTime } from 'luxon'

import DataGridEditableTextField from '@/components/dataDisplay/DataGridEditableTextField'
import type { MarketProgram } from '@/features/bidding/constants'
import { BidType, DEFAULT_CURRENCY, ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU } from '@/features/bidding/constants'
import type { AggregatedBid, AggregatedBidPtu } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import {
  getCountOfNonZeroPriceQuantities,
  getUniquePricesFromAllActivationGroups,
  isPortfolioEntelios,
} from '@/features/bidding/utils/validation/enteliosValidationUtils'
import { isCapacityValid } from '@/features/bidding/utils/validation/isCapacityValid'

type GetPriceBidDataGridColumnsProps = {
  acceptsOnlyPositiveInteger?: boolean
  aggregatedBid: AggregatedBid
  activationGroupUuids: string[] // Activation groups that are part of the aggregated bid
  bidsPerActivationGroup?: Map<string, AggregatedBidWrapper>
  editable: boolean
  t: TFunction
  locale: string
  displayChip?: boolean
  isPtuEditable?: (ptuStart: DateTime, bidType: BidType, marketProgram?: MarketProgram) => boolean
  isPtuMarkedAsResetForActivationGroup?: (activationGroupUuid: string, ptuStart: DateTime) => boolean
  bidType?: BidType
  marketProgram?: MarketProgram
  portfolioCode?: string
  countryCode?: string
  nextPtuToExpire?: DateTime | null
}

export function getPriceBidDataGridColumns({
  acceptsOnlyPositiveInteger = false,
  aggregatedBid,
  activationGroupUuids,
  bidsPerActivationGroup,
  editable,
  t,
  locale,
  displayChip = false,
  isPtuEditable,
  isPtuMarkedAsResetForActivationGroup,
  bidType,
  marketProgram,
  portfolioCode,
  countryCode,
  nextPtuToExpire,
}: GetPriceBidDataGridColumnsProps): GridColDef<AggregatedBidPtu>[] {
  return [
    {
      field: 'ptuStart',
      headerName: t('bidding.create_bid.header.ptu'),
      valueFormatter: (ptuStart: DateTime) => ptuStart.toFormat('HH:mm'),
      minWidth: 60,
      headerClassName: 'default-header',
    },
    {
      field: 'available',
      headerName: t('bidding.create_bid.header.available'),
      sortable: false,
      valueFormatter: (available: number) => `${available} ${aggregatedBid.unit}`,
      flex: 1,
      minWidth: 90,
      headerClassName: 'default-header',
      renderCell: (params: GridRenderEditCellParams<AggregatedBidPtu>) =>
        displayChip ? <Chip label={params.formattedValue} /> : params.formattedValue,
    },
    {
      field: 'remaining',
      headerName: t('bidding.create_bid.header.remaining'),
      sortable: false,
      valueFormatter: (remaining: number) => `${remaining} ${aggregatedBid.unit}`,
      flex: 1,
      minWidth: 100,
      headerClassName: 'default-header',
      cellClassName: (params: GridCellParams<AggregatedBidPtu>) => getNonZeroValueClassName(params.row.remaining),
      renderCell: (params: GridRenderEditCellParams<AggregatedBidPtu>) =>
        displayChip ? (
          <Chip classes={{ label: getNonZeroValueClassName(params.row.remaining) }} label={params.formattedValue} />
        ) : (
          params.formattedValue
        ),
    },
    ...getPriceChunkColumns({
      aggregatedBid,
      activationGroupUuids,
      bidsPerActivationGroup,
      editable,
      acceptsOnlyPositiveInteger,
      locale,
      t,
      isPtuEditable,
      isPtuMarkedAsResetForActivationGroup,
      bidType,
      marketProgram,
      countryCode,
      portfolioCode,
      nextPtuToExpire,
    }),
  ]
}

type GetPriceChunkColumnsParams = {
  aggregatedBid: AggregatedBid
  activationGroupUuids: string[] // Activation groups that are part of the aggregated bid
  bidsPerActivationGroup?: Map<string, AggregatedBidWrapper>
  editable: boolean
  acceptsOnlyPositiveInteger: boolean
  locale: string
  t: TFunction
  isPtuEditable?: (ptuStart: DateTime, bidType: BidType, marketProgram?: MarketProgram) => boolean
  isPtuMarkedAsResetForActivationGroup?: (activationGroupUuid: string, ptuStart: DateTime) => boolean
  bidType?: BidType
  marketProgram?: MarketProgram
  countryCode?: string
  portfolioCode?: string
  nextPtuToExpire?: DateTime | null
}

const getPriceChunkColumns = ({
  aggregatedBid,
  activationGroupUuids,
  bidsPerActivationGroup,
  editable,
  acceptsOnlyPositiveInteger,
  locale,
  t,
  isPtuEditable,
  isPtuMarkedAsResetForActivationGroup,
  bidType,
  marketProgram,
  countryCode,
  portfolioCode,
  nextPtuToExpire,
}: GetPriceChunkColumnsParams): GridColDef<AggregatedBidPtu>[] => {
  const prices = aggregatedBid.ptus.flatMap((rowItem) =>
    rowItem.priceQuantities.map((priceCapacity) => priceCapacity.price),
  )
  const uniquePrices = Array.from(new Set(prices)).sort((a, b) => a - b)

  return uniquePrices.map((price, priceIndex) => {
    const formattedPrice = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: DEFAULT_CURRENCY,
    }).format(price)

    return {
      field: `price_${price}`,
      editable: editable,
      sortable: false,
      headerName: formattedPrice,
      flex: 1,
      minWidth: 90,
      valueFormatter: (value: number) => `${value ?? '-'}`,
      valueGetter: (_, row) => row.priceQuantities.find((pc) => pc.price === price)?.quantity,
      valueSetter: (value: number, row): AggregatedBidPtu => {
        const hasExistingEntry = row.priceQuantities.some((priceCapacity) => priceCapacity.price === price)
        // A cell with no existing entry (rendered as "-") starts edit mode with an `undefined`
        // value, and clearing the field yields `""`. Committing either on such a cell must leave
        // the PTU untouched. Clearing a cell that *does* have an entry still means 0,
        // since that is how a standing offer is deliberately withdrawn.
        const isBlankValue = value === undefined || String(value).trim() === ''
        if (isBlankValue && !hasExistingEntry) {
          return row
        }

        const quantity = Number(value)
        if (Number.isNaN(quantity)) {
          return row
        }

        return {
          ...row,
          // A PTU may not carry an entry for every price column (e.g. differing price ladders across
          // PTUs), which renders as "-". Editing such a cell must add a new entry, not just update one.
          priceQuantities: hasExistingEntry
            ? row.priceQuantities.map((priceCapacity) =>
                priceCapacity.price === price ? { ...priceCapacity, quantity } : priceCapacity,
              )
            : [...row.priceQuantities, { price, quantity }],
        }
      },
      isEditable: (params) => {
        if (!editable || !isPtuEditable || !bidType) return editable
        return isPtuEditable(params.row.ptuStart, bidType, marketProgram)
      },
      renderCell: (params) => {
        if (!editable || !bidType || !marketProgram) return undefined

        const isPtuEditableValue = isPtuEditable ? isPtuEditable(params.row.ptuStart, bidType, marketProgram) : true
        const isRowEditable = isPtuEditableValue
        const wasReset = isPtuMarkedAsResetForActivationGroup
          ? activationGroupUuids.some((ag) => isPtuMarkedAsResetForActivationGroup(ag, params.row.ptuStart))
          : false

        const aboutToBeDisabled = !!nextPtuToExpire && params.row.ptuStart.equals(nextPtuToExpire)

        const enteliosTooManyPriceSteps =
          isPortfolioEntelios(portfolioCode) &&
          hasMorePriceStepsThanEnteliosAllows(params.row, bidsPerActivationGroup, countryCode, bidType)

        let tooltipTitle = ''
        if (wasReset) {
          tooltipTitle = t('bidding.ptu.was_reset')
        } else if (!isRowEditable) {
          tooltipTitle = t('bidding.ptu.not_editable')
        } else if (enteliosTooManyPriceSteps) {
          tooltipTitle = t('bidding.ptu.entelios_price_step_limit_exceeded')
        } else if (aboutToBeDisabled) {
          tooltipTitle = t('bidding.ptu.about_to_be_disabled')
        }

        const fieldElement = (
          <DataGridEditableTextField readonlyField disabled={!isRowEditable} value={params.formattedValue} />
        )

        return tooltipTitle ? (
          <Tooltip arrow title={tooltipTitle}>
            <span>{fieldElement}</span>
          </Tooltip>
        ) : (
          fieldElement
        )
      },
      renderEditCell: (params: GridRenderEditCellParams<AggregatedBidPtu>) => {
        if (!isPtuEditable) return

        const isRowEditable = isPtuEditable(params.row.ptuStart, bidType!, marketProgram)

        // Automatically stop edit mode if PTU is not editable
        if (!isRowEditable) {
          setTimeout(() => {
            params.api.stopCellEditMode({ id: params.id, field: params.field })
          }, 0)
        }

        return (
          <DataGridEditableTextField
            acceptsOnlyPositiveInteger={acceptsOnlyPositiveInteger}
            disabled={!isRowEditable}
            value={params.value}
            onChange={(value: string | undefined) => {
              if (value && !isCapacityValid(value)) return

              params.api.setEditCellValue({ ...params, value })
            }}
          />
        )
      },
      headerClassName: () => getClassName(priceIndex),
      cellClassName: (params) =>
        getCellClassName(params, priceIndex, countryCode, portfolioCode, bidType, bidsPerActivationGroup),
    }
  })
}

const getClassName = (priceIndex: number) => {
  if (priceIndex % 2 === 0) {
    return 'editable-column-light'
  } else {
    return 'editable-column-dark'
  }
}

const getNonZeroValueClassName = (value: number) => {
  return value !== 0 ? 'error-value' : ''
}

const getCellClassName = (
  params,
  priceIndex: number,
  countryCode?: string,
  portfolioCode?: string,
  bidType?: BidType,
  bidsPerActivationGroup?: Map<string, AggregatedBidWrapper>,
) => {
  if (
    isPortfolioEntelios(portfolioCode) &&
    hasMorePriceStepsThanEnteliosAllows(params.row, bidsPerActivationGroup, countryCode, bidType)
  ) {
    return 'error-value'
  } else {
    return getClassName(priceIndex)
  }
}

const hasMorePriceStepsThanEnteliosAllows = (
  ptu: AggregatedBidPtu,
  bidsPerActivationGroup?: Map<string, AggregatedBidWrapper>,
  countryCode?: string,
  bidType?: BidType,
) => {
  return (
    getCountOfNonZeroPriceQuantities(ptu) > ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU ||
    // Additional SE Entelios rule for CAPACITY bids: only one price allowed per PTU for all Activation Groups
    (countryCode?.toUpperCase() === 'SE' &&
      bidType === BidType.CAPACITY &&
      getUniquePricesFromAllActivationGroups(ptu, bidsPerActivationGroup!) > ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU)
  )
}
